import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl } from 'react-intl';

import {
  catalogBibShape,
  libraryShape,
  listShape,
  paginationShape,
  userIdentityShape,
  routerShape
} from '@bibliocommons/bc-prop-types';
import { withRouter } from '@bibliocommons/utils-routing';

import {
  trackContentClick as trackContentClickAction,
  trackContentImpression as trackContentImpressionAction
} from 'app/actions/AnalyticsGa4Actions';
import ListsActions from 'app/actions/ListsActions';
import { getRelationships } from 'app/actions/RelationshipsActions';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { UI_COMPONENT_TYPE, UI_CONTAINER_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { LIST_CATEGORIES } from 'app/constants/ListsConstants';
import { selectCatalogBib } from 'app/selectors/catalogBibs/CatalogBibSelector';
import { selectUserEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { selectLists, selectListsPagination } from 'app/selectors/ListsSelector';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import AllListsPage from './AllListsPage';

export function AllListsPageContainer(props) {
  const { catalogBib, intl, lists, trackContentClick, trackContentImpression } = props;

  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    if (currentUser?.isEmpty() ?? true) {
      return;
    }

    const listOwnerIds = lists?.map(list => list.getIn(['owner', 'id']));

    dispatch(getRelationships({ userId: currentUser.get('id'), userIds: listOwnerIds }));
  }, [currentUser, dispatch, lists]);

  const title = catalogBib.getIn(['brief', 'title']);
  const pageTitle = intl.formatMessage({ id: 'all_lists_page_title' }, { title });

  function handleListImpression({ impressionId, listId }) {
    trackContentImpression({
      listId,
      properties: {
        impression_id: impressionId
      },
      ui: {
        ui_container_type: UI_CONTAINER_TYPE.ITEM
      }
    });
  }

  function handleListTitleClick({ impressionId, listId, listTitle }) {
    trackContentClick({
      listId,
      properties: {
        impression_id: impressionId
      },
      ui: {
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
        ui_component_label: listTitle
      }
    });
  }

  function handleListImageClick({ impressionId, listId }) {
    trackContentClick({
      listId,
      properties: {
        impression_id: impressionId
      },
      ui: {
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE
      }
    });
  }

  return (
    <div>
      <DocumentHeadContainer pageTitle={pageTitle} />
      <AllListsPage
        {...props}
        handleListTitleClick={handleListTitleClick}
        handleListImageClick={handleListImageClick}
        handleListImpression={handleListImpression}
      />
    </div>
  );
}

AllListsPageContainer.propTypes = {
  catalogBib: catalogBibShape.isRequired,
  category: PropTypes.string.isRequired,
  library: libraryShape.isRequired,
  lists: ImmutablePropTypes.listOf(listShape).isRequired,
  users: ImmutablePropTypes.mapOf(userIdentityShape).isRequired,
  pagination: paginationShape.isRequired,
  listsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  router: routerShape.isRequired,
  isFetching: PropTypes.bool,
  trackContentClick: PropTypes.func.isRequired,
  trackContentImpression: PropTypes.func.isRequired
};

const mapStateToProps = (state, { router }) => ({
  catalogBib: selectCatalogBib(state),
  category: router.query.category || LIST_CATEGORIES.STAFF,
  library: selectCurrentLibrary(state),
  lists: selectLists(state),
  pagination: selectListsPagination(state),
  users: selectUserEntities(state),
  isFetching: state.getIn(['lists', 'lists', 'isFetching'], false)
});

const mapDispatchToProps = dispatch => ({
  listsActions: bindActionCreators(ListsActions, dispatch),
  trackContentClick: bindActionCreators(trackContentClickAction, dispatch),
  trackContentImpression: bindActionCreators(trackContentImpressionAction, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(AllListsPageContainer)));
